import * as Cesium from "cesium";
import { create } from "zustand";

export type MeasurementType = "distance-ground" | "height-from-ground";

export interface SerializedMeasurementPoint {
  x: number;
  y: number;
  z: number;
}

export interface ViewerMeasurement {
  id: string;
  type: MeasurementType;
  startPoint: SerializedMeasurementPoint;
  endPoint: SerializedMeasurementPoint;
  distanceMeters: number;
  distanceLabel: string;
  createdAt: string;
}

interface MeasurementStoreState {
  measurements: ViewerMeasurement[];
  isMeasurementModeActive: boolean;
  measurementType: MeasurementType;
  pendingFirstPoint: SerializedMeasurementPoint | null;
}

interface MeasurementStoreActions {
  setMeasurementType: (type: MeasurementType) => void;
  setMeasurementModeActive: (isActive: boolean) => void;
  startMeasurementSession: () => void;
  stopMeasurementSession: () => void;
  setPendingFirstPoint: (point: Cesium.Cartesian3 | null) => void;
  addMeasurement: (payload: {
    type: MeasurementType;
    startPoint: Cesium.Cartesian3;
    endPoint: Cesium.Cartesian3;
    distanceMeters: number;
  }) => ViewerMeasurement;
  updateMeasurement: (payload: {
    id: string;
    startPoint: Cesium.Cartesian3;
    endPoint: Cesium.Cartesian3;
    distanceMeters: number;
  }) => void;
  clearAllMeasurements: () => void;
}

type MeasurementStore = MeasurementStoreState & MeasurementStoreActions;

const STORAGE_KEY = "pixatwin:viewer:measurements";
const INITIAL_MEASUREMENT_TYPE: MeasurementType = "distance-ground";

declare global {
  interface Window {
    __PIXATWIN_MEASUREMENTS_BOOTSTRAPPED__?: boolean;
  }
}

const formatOneDecimal = (value: number): string =>
  value.toFixed(1).replace(".", ",");

export const formatDistanceLabel = (distanceMeters: number): string => {
  const metersLabel = formatOneDecimal(distanceMeters);
  return `${metersLabel} m`;
};

const isStorageAvailable = (): boolean =>
  typeof localStorage?.getItem === "function" &&
  typeof localStorage?.setItem === "function" &&
  typeof localStorage?.removeItem === "function";

const serializePoint = (
  point: Cesium.Cartesian3,
): SerializedMeasurementPoint => ({
  x: point.x,
  y: point.y,
  z: point.z,
});

export const deserializePoint = (
  point: SerializedMeasurementPoint,
): Cesium.Cartesian3 => new Cesium.Cartesian3(point.x, point.y, point.z);

const isSerializedPoint = (
  value: unknown,
): value is SerializedMeasurementPoint => {
  if (!value || typeof value !== "object") return false;

  const record = value as Record<string, unknown>;
  return (
    typeof record.x === "number" &&
    Number.isFinite(record.x) &&
    typeof record.y === "number" &&
    Number.isFinite(record.y) &&
    typeof record.z === "number" &&
    Number.isFinite(record.z)
  );
};

const isMeasurementType = (value: unknown): value is MeasurementType =>
  value === "distance-ground" || value === "height-from-ground";

const sanitizeMeasurement = (value: unknown): ViewerMeasurement | null => {
  if (!value || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  if (
    typeof record.id !== "string" ||
    !record.id ||
    !isMeasurementType(record.type) ||
    !isSerializedPoint(record.startPoint) ||
    !isSerializedPoint(record.endPoint) ||
    typeof record.distanceMeters !== "number" ||
    !Number.isFinite(record.distanceMeters)
  ) {
    return null;
  }

  return {
    id: record.id,
    type: record.type,
    startPoint: record.startPoint,
    endPoint: record.endPoint,
    distanceMeters: record.distanceMeters,
    distanceLabel:
      typeof record.distanceLabel === "string"
        ? record.distanceLabel
        : formatDistanceLabel(record.distanceMeters),
    createdAt:
      typeof record.createdAt === "string"
        ? record.createdAt
        : new Date().toISOString(),
  };
};

const readMeasurementsFromStorage = (): ViewerMeasurement[] => {
  if (!isStorageAvailable()) return [];

  try {
    const rawValue = localStorage.getItem(STORAGE_KEY);
    if (!rawValue) return [];

    const parsedValue: unknown = JSON.parse(rawValue);
    if (!Array.isArray(parsedValue)) return [];

    return parsedValue
      .map((item) => sanitizeMeasurement(item))
      .filter((item): item is ViewerMeasurement => item !== null);
  } catch (error) {
    console.warn(
      "[MeasurementStore] Impossible de lire les mesures depuis le stockage local.",
      error,
    );
    return [];
  }
};

const writeMeasurementsToStorage = (measurements: ViewerMeasurement[]) => {
  if (!isStorageAvailable()) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(measurements));
  } catch (error) {
    console.warn(
      "[MeasurementStore] Impossible d'écrire les mesures dans le stockage local.",
      error,
    );
  }
};

const clearMeasurementsStorage = () => {
  if (!isStorageAvailable()) return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn(
      "[MeasurementStore] Impossible de nettoyer le stockage des mesures.",
      error,
    );
  }
};

const ensureEphemeralStorageBehavior = () => {
  if (typeof window === "undefined") return;
  if (window.__PIXATWIN_MEASUREMENTS_BOOTSTRAPPED__) return;

  // Exigence produit: les mesures sont locales et supprimées au rechargement.
  clearMeasurementsStorage();

  window.addEventListener("beforeunload", () => {
    clearMeasurementsStorage();
  });

  window.__PIXATWIN_MEASUREMENTS_BOOTSTRAPPED__ = true;
};

const buildMeasurementId = (): string => {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `mesure-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

ensureEphemeralStorageBehavior();

export const useMeasurementStore = create<MeasurementStore>((set, get) => ({
  measurements: readMeasurementsFromStorage(),
  isMeasurementModeActive: false,
  measurementType: INITIAL_MEASUREMENT_TYPE,
  pendingFirstPoint: null,

  setMeasurementType: (type: MeasurementType) => {
    set({
      measurementType: type,
      // Évite de terminer une mesure commencée avec un autre mode.
      pendingFirstPoint: null,
    });
  },

  setMeasurementModeActive: (isActive: boolean) => {
    set({
      isMeasurementModeActive: isActive,
      pendingFirstPoint: isActive ? get().pendingFirstPoint : null,
    });
  },

  startMeasurementSession: () => {
    set({
      isMeasurementModeActive: true,
      pendingFirstPoint: null,
    });
  },

  stopMeasurementSession: () => {
    set({
      isMeasurementModeActive: false,
      pendingFirstPoint: null,
    });
  },

  setPendingFirstPoint: (point: Cesium.Cartesian3 | null) => {
    set({
      pendingFirstPoint: point ? serializePoint(point) : null,
    });
  },

  addMeasurement: ({ type, startPoint, endPoint, distanceMeters }) => {
    const safeDistance = Math.max(0, distanceMeters);
    const newMeasurement: ViewerMeasurement = {
      id: buildMeasurementId(),
      type,
      startPoint: serializePoint(startPoint),
      endPoint: serializePoint(endPoint),
      distanceMeters: safeDistance,
      distanceLabel: formatDistanceLabel(safeDistance),
      createdAt: new Date().toISOString(),
    };

    set((state) => {
      const nextMeasurements = [...state.measurements, newMeasurement];
      writeMeasurementsToStorage(nextMeasurements);

      return {
        measurements: nextMeasurements,
      };
    });

    return newMeasurement;
  },

  updateMeasurement: ({ id, startPoint, endPoint, distanceMeters }) => {
    const safeDistance = Math.max(0, distanceMeters);

    set((state) => ({
      measurements: state.measurements.map((measurement) =>
        measurement.id === id
          ? {
              ...measurement,
              startPoint: serializePoint(startPoint),
              endPoint: serializePoint(endPoint),
              distanceMeters: safeDistance,
              distanceLabel: formatDistanceLabel(safeDistance),
            }
          : measurement,
      ),
    }));
  },

  clearAllMeasurements: () => {
    clearMeasurementsStorage();
    set({
      measurements: [],
      pendingFirstPoint: null,
    });
  },
}));
